import { createPagination } from "./utilities/paginationBuilder";
import { getFilterClause, createPartialURL } from "./utilities/urlBuilder";
import { Deserializer } from "jsonapi-serializer";
import { CommonZone } from "../models/CommonZone";
import { CommonZoneWrapper } from "../models/wrappers/CommonZoneWrapper";
import { ListQuery } from "../models/ListQuery";
import { APIConnector } from "./api/APIConnector";
import { PaginationModel } from "../models/Pagination";
import { Name } from "../models/Name";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { genericError } from "../models/GenericError";
import { BAD_REQUEST, NO_CONTENT } from "../constants/response-status-codes";
import { INTERNAL_SERVER_ERROR } from "../constants/response-status-codes";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { isSuccessStatus } from "../utilities/requestResponseValidator";

const apiConnector = new APIConnector();

export const getCommonZones = async (listParameters?: ListQuery): Promise<CommonZoneWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm)
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const fieldsCommonZone = 'fields[commonZone]=czName,czDesc,totalZones,site';
        const fieldsSite = 'fields[site]=siteName';
        const url = `commonZone?include=site${partialURL}&${fieldsCommonZone}&${fieldsSite}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {

            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const commonZone: CommonZone[] = data.map((commonZone): CommonZone => {
                    return {
                        id: commonZone['id'],
                        name: commonZone['cz-name'],
                        zones: commonZone['total-zones'],
                        site: commonZone['site']['site-name'],
                    }
                })

                return resolve({ rows: commonZone, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getCommonZoneNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        let url = `commonZone?fields[commonZone]=czName${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {

            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const commonZoneNames: Name[] = data.map((commonZone): Name => {
                    return { value: commonZone['id'], label: commonZone['cz-name'] };
                });

                return resolve({ rows: commonZoneNames, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getCommonZoneById = async (commonZoneId: string): Promise<CommonZone> => {
    try {        
        const commonZoneFields = 'fields[commonZone]=czName,czDesc,totalZones,site'
        const siteFields = 'fields[site]=siteName'
        const url = `commonZone/${commonZoneId}?include=site&${commonZoneFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const commonZone: CommonZone = {
                    id: data['id'],
                    name: data['cz-name'],
                    description: data['cz-desc'] ?? '',
                    site: data['site']['site-name'],
                    siteId: data['site']['id'],
                    zones: data['total-zones'],
                }

                return resolve(commonZone);
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const assignZone = async (commonZoneId: string, toBeAdded: string[], toBeRemoved: string[]): Promise<any> => {
    try {
        // Unassign 
        const removeResponse = toBeRemoved.map(async (zoneId) => {
            const data = {
                data: {
                    type: 'commonZone',
                    id: commonZoneId
                }
            }

            return await apiConnector.deleteData(`zone/${zoneId}/relationships/commonZone/${commonZoneId}`, data);
        });

        
        // Assign
        const addResponse = toBeAdded.map(async (zoneId) => {
            const data = {
                data: {
                    type: 'commonZone',
                    id: commonZoneId
                }
            }

            return await apiConnector.sendData(`zone/${zoneId}/relationships/commonZone`, false, data);
        });

        return Promise.allSettled([removeResponse, addResponse]);
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}



export const storeCommonZone = async (commonZone: CommonZone, createCommonZone = true): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            data: {
                type: 'commonZone',
                id: commonZone.id,
                attributes: {
                    czDesc: commonZone.description,
                    czName: commonZone.name,
                    czNo: commonZone.id
                },
                relationships: {
                    site: {
                        data: {
                            type: 'site',
                            id: commonZone.siteId
                        }
                    }
                }
            }
        }

        const url = `commonZone${createCommonZone ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createCommonZone, newRecord).then(data => {
            response = data;
        }).catch(error => {
            console.log('Save common zone error: ', error);
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createCommonZone) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }
                    new Deserializer().deserialize(content, (error, newCommonZone) => {
                        if (error) {
                            return reject({ status: response.status, id: newCommonZone.id, success: false, message: genericError });
                        }
                        return resolve({ id: newCommonZone.id, success: true });
                    })
                    return;
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: commonZone.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: commonZone.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: commonZone.id, success: false, message: genericError });
        })

    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteCommonZone = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `commonZone/${id}`;
        const commonZone = {
            data: {
                type: "commonZone",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, commonZone)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }
            const json = await response.json();

            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
